import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import {
    clearUserNotificationsDetail,
    getUserNotificationsDetailFetch,
    updateUserNotificationsDetailFetch,
} from 'src/resources/userNotificationsDetail/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import UserNotificationsEditor, {
    UserNotificationsEditorHandle,
} from 'src/components/UserNotifications';
import { getUsersListFetch } from '../../resources/usersList/reducer';

const UserNotificationsDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const { idUser } = useParams();
    const notificationsRef = useRef<UserNotificationsEditorHandle>(null);

    const data = useSelector((state: RootStoreType) => state.userNotificationsDetail.data);
    const users = useSelector((state: RootStoreType) => state.usersList.data);
    const user = users?.find((listUser) => listUser.USER_ID === Number(idUser));
    const loading = useSelector((state: RootStoreType) => state.userNotificationsDetail.loading);
    const userPermissions = useSelector((state: RootStoreType) => state.user.data?.permissions);

    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionUpdate = useCheckPermission('USER.NOTIFICATION.UPDATE');
    const backUrl = '/users/';

    useEffect(() => {
        if (idUser) {
            dispatch(
                setLocationParams({
                    name: translate('userNotificationsDetail.title', {
                        idUser,
                        email: user?.USER_EMAIL || '',
                    }),
                    backUrl,
                })
            );
        }
    }, [idUser, backUrl, user, dispatch]);

    useEffect(() => {
        if (!users) {
            dispatch(getUsersListFetch());
        }
    }, [users, dispatch]);

    useEffect(() => {
        if (idUser) {
            dispatch(getUserNotificationsDetailFetch(idUser));
        }
        return () => {
            dispatch(clearUserNotificationsDetail());
        };
    }, [idUser, dispatch]);

    const handleSave = () => {
        setConfirmParams({
            title: translate('userNotificationsDetail.save'),
            content: translate('userNotificationsDetail.saveConfirm'),
            action: () => {
                const formatData = notificationsRef.current?.getPayload();
                if (formatData) {
                    dispatch(updateUserNotificationsDetailFetch({ idUser, data: formatData }));
                }
            },
        });
    };

    return (
        <>
            <TopBlock permissionUpdate={permissionUpdate} handleSave={handleSave} />
            {loading && <Loading />}

            <UserNotificationsEditor
                ref={notificationsRef}
                notifications={data}
                permissions={userPermissions}
                disabledSelect={!permissionUpdate}
                loading={!!loading}
            />

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): void => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default UserNotificationsDetailContainer;
